import React, { useEffect, useState } from 'react';
import * as api from '../../api/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './GoalEditor.css';

const GoalEditor = ({
    goal,
    deleteGoal,
    unitSettings,
    isNew = false,
    onSave,
    onCancel,
}) => {

    const [originalStateGoal, setOriginalStateGoal] = useState(null);
    const [stateGoal, setStateGoal] = useState(null);
    const [goalHumanReadableValue, setGoalHumanReadableValue] = useState(null);
    const [goalActivityTypesArray, setGoalActivityTypesArray] = useState(null);
    const [showActivityHint, setShowActivityHint] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Conversion constants
    const METRES_IN_KILOMETRE = 1000;
    const METRES_IN_MILE = 1609.34;
    const METRES_TO_FEET = 3.28084;

    const getGoalValueUnitConvertedHumanReadable = React.useCallback((goal) => {
        if (goal.goalUnit === "DISTANCE") {
            if (unitSettings.distance_unit === 'MILES') {
                return (goal.goalValue / METRES_IN_MILE).toFixed(2);
            } else {
                return (goal.goalValue / METRES_IN_KILOMETRE).toFixed(2);
            }
        } else if (goal.goalUnit === "TIME") {
            return (goal.goalValue / 3600).toFixed(2);
        } else if (goal.goalUnit === "ACTIVITY_COUNT") {
            return goal.goalValue;
        } else if (goal.goalUnit === "ELEVATION_GAIN") {
            if (unitSettings.elevation_unit === 'FEET') {
                return (goal.goalValue * METRES_TO_FEET).toFixed(0);
            } else {
                return goal.goalValue;
            }
        } else {
            return goal.goalValue;
        }
    }, [unitSettings]);

    useEffect(() => {
        if (goal) {
            setGoalHumanReadableValue(getGoalValueUnitConvertedHumanReadable(goal));
            setGoalActivityTypesArray(Array.isArray(goal.activityTypes) ? goal.activityTypes : goal.activityTypes.split(",").filter(Boolean));
            const activityHintKey = `goal-activity-hint-seen-${goal.goalId}`;
            const hasSeenActivityHint = localStorage.getItem(activityHintKey) === 'true';
            setShowActivityHint(!hasSeenActivityHint);
            if (!hasSeenActivityHint) {
                localStorage.setItem(activityHintKey, 'true');
            }
            setOriginalStateGoal(goal);
            setStateGoal(goal);
        }
    }, [goal, unitSettings, getGoalValueUnitConvertedHumanReadable]);

    const reset = () => {
        setSaveError(null);
        setStateGoal(originalStateGoal);
        setGoalHumanReadableValue(getGoalValueUnitConvertedHumanReadable(originalStateGoal));
        setGoalActivityTypesArray(Array.isArray(originalStateGoal.activityTypes) ? originalStateGoal.activityTypes : originalStateGoal.activityTypes.split(",").filter(Boolean));
    }

    const getGoalValueUnitConvertedIso = (goal) => {
        if (goal.goalUnit === "DISTANCE") {
            if (unitSettings.distance_unit === 'MILES') {
                return parseInt(goalHumanReadableValue * METRES_IN_MILE);
            } else {
                return parseInt(goalHumanReadableValue * METRES_IN_KILOMETRE);
            }
        } else if (goal.goalUnit === "TIME") {
            return goalHumanReadableValue * 3600;
        } else if (goal.goalUnit === "ACTIVITY_COUNT") {
            return goalHumanReadableValue;
        } else if (goal.goalUnit === "ELEVATION_GAIN") {
            if (unitSettings.elevation_unit === 'FEET') {
                return parseInt(goalHumanReadableValue / METRES_TO_FEET);
            } else {
                return goalHumanReadableValue;
            }
        } else {
            return goalHumanReadableValue;
        }           
    }

    const getGoalUnitConvertedIso = (goal) => {
        if (goal.goalUnit === "DISTANCE") {
            return unitSettings.distance_unit === 'MILES' ? 'Miles' : 'Km';
        } else if (goal.goalUnit === "TIME") {
            return "Hours";
        } else if (goal.goalUnit === "ACTIVITY_COUNT") {
            return "Activity Count";
        } else if (goal.goalUnit === "ELEVATION_GAIN") {
            return unitSettings.elevation_unit === 'FEET' ? 'Ft' : 'Metres';
        } else {
            return "Value";
        }           
    }


    return (
        <div className="goal-editor-wrap">
            {stateGoal ? (
                <div className="goal-editor">
                    <section className="editor-section">
                        <label htmlFor={`goal-name-${goal.goalId}`}>Goal name</label>
                    <input 
                        id={`goal-name-${goal.goalId}`}
                        className="editor-input"
                        type="text" 
                        value={stateGoal.goalName}
                        placeholder={isNew ? "New Goal" : ""}
                        onChange={(e) => setStateGoal({...stateGoal, goalName: e.target.value})}
                    /> 
                    <div className="date-grid">
                    <div className="field-group">
                    <label htmlFor={`goal-start-${goal.goalId}`}>Start date</label>
                    <DatePicker
                        id={`goal-start-${goal.goalId}`}
                        className="editor-input"
                        selected={stateGoal.startDate ? new Date(stateGoal.startDate) : null}
                        onChange={(date) => setStateGoal({...stateGoal, startDate: date.toISOString()})}
                        placeholderText="Select a date"
                        dateFormat="do MMM yyyy"
                        showPopperArrow={false}
                    />
                    </div>
                    <div className="field-group">
                    <label htmlFor={`goal-end-${goal.goalId}`}>End date</label>
                    <DatePicker
                        id={`goal-end-${goal.goalId}`}
                        className="editor-input"
                        selected={stateGoal.endDate ? new Date(stateGoal.endDate) : null}
                        onChange={(date) => setStateGoal({...stateGoal, endDate: date.toISOString()})}
                        placeholderText="Select a date"
                        dateFormat="do MMM yyyy"
                        showPopperArrow={false}
                    />
                    </div>
                    </div>
                    </section>
                    <section className="editor-section">
                        <div className="measurement-grid">
                        <div className="field-group">
                        <label htmlFor={`goal-value-${goal.goalId}`}>{getGoalUnitConvertedIso(stateGoal)} target</label>
                    <input 
                        id={`goal-value-${goal.goalId}`}
                        className="editor-input"
                        type="text" 
                        value={goalHumanReadableValue}
                        onChange={(e) => setGoalHumanReadableValue(e.target.value)}
                    /> 
                    </div>
                    <div className="field-group">
                    <label htmlFor={`goal-unit-${goal.goalId}`}>Goal unit</label>
                    <select 
                        id={`goal-unit-${goal.goalId}`}
                        className="editor-input"
                        value={stateGoal.goalUnit}
                        onChange={(e) => setStateGoal({...stateGoal, goalUnit: e.target.value})}
                    > 
                        <option value="DISTANCE">Distance</option>
                        <option value="TIME">Time</option>
                        <option value="ACTIVITY_COUNT">Activity Count</option>
                        <option value="ELEVATION_GAIN">Elevation Gain</option>
                    </select>
                    </div>
                    </div>
                    </section>
                    <section className="editor-section">
                        <label className="checkbox-field" htmlFor={`goal-include-in-plan-${goal.goalId}`}>
                            <input
                                id={`goal-include-in-plan-${goal.goalId}`}
                                type="checkbox"
                                checked={stateGoal.includeInPlan !== false}
                                onChange={(e) => setStateGoal({ ...stateGoal, includeInPlan: e.target.checked })}
                            />
                            Include in Plan view
                        </label>
                    </section>
                    <section className="editor-section activity-section">
                        <h3>Activities to include</h3>
                        {showActivityHint && <p>Select one or more activity types for this goal.</p>}
                        <div className="activity-options" role="group" aria-label="Activities to include">
                            {[
                                ["RUN", "Run"],
                                ["RIDE", "Ride"],
                                ["SWIM", "Swim"],
                                ["HIKE", "Hike"],
                                ["WALK", "Walk"],
                                ["WORKOUT", "Workout"],
                                ["WEIGHTTRAINING", "Weight Training"],
                                ["STAIRSTEPPER", "Stair Stepper"],
                            ].map(([value, label]) => {
                                const isSelected = goalActivityTypesArray.includes(value);
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        className={`activity-option${isSelected ? " selected" : ""}`}
                                        aria-pressed={isSelected}
                                        onClick={() => setGoalActivityTypesArray(
                                            isSelected
                                                ? goalActivityTypesArray.filter(activity => activity !== value)
                                                : [...goalActivityTypesArray, value]
                                        )}
                                    >
                                        <span aria-hidden="true">{isSelected ? "✓" : "+"}</span>
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                    <div className="editor-actions">
                    <button
                        className="save-button"
                        type="button"
                        disabled={isSaving}
                        onClick={async () => {
                            const goalToSave = {
                                ...stateGoal,
                                goalValue: getGoalValueUnitConvertedIso(stateGoal),
                                activityTypes: goalActivityTypesArray.join(","),
                            };
                            if (!Number.isFinite(Number(goalHumanReadableValue)) || Number(goalHumanReadableValue) <= 0) {
                                setSaveError('Enter a target greater than 0 to save this goal.');
                                return;
                            }
                            setSaveError(null);
                            setIsSaving(true);
                            try {
                                if (isNew) {
                                    await api.createGoal(goalToSave);
                                    onSave?.(goalToSave.goalName || 'New Goal');
                                } else {
                                    await api.updateGoal(goalToSave);
                                    setStateGoal(goalToSave);
                                    setOriginalStateGoal(goalToSave);
                                    onSave?.('Goal updated.', goalToSave.goalName);
                                }
                            } catch (e) {
                                setSaveError(api.getErrorMessage(e, 'Unable to save this goal.'));
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                    >{isSaving ? 'Saving...' : 'Save'}</button>
                    <button
                        className="reset-button"
                        type="button"
                        onClick={reset}
                    >Reset changes</button>
                    <button
                        className="delete-button"
                        type="button"
                        onClick={isNew ? onCancel : deleteGoal}
                    >Delete goal</button>
                    </div>
                    {saveError && <div className="goal-save-error" role="alert">{saveError}</div>}
                </div>
            ) : (
                <p>Failed to load goal</p>
            )}
        </div>
    );

}

export default GoalEditor;